import React, { useState, useEffect } from "react";
import './Gallery.css';
import { INSTAGRAM } from "../../config";

// Feed JSON do Behold (configurado para devolver só posts com #hmescolakickboxing)
const FEED_URL = process.env.REACT_APP_BEHOLD_FEED_URL;
// Perfil mostrado quando não há vídeos (definido em src/config.js)
const PERFIL_INSTAGRAM = INSTAGRAM;

const MAX_VIDEOS = 6;
const LIMITE_LEGENDA = 100;

const isHttps = (url) => {
    try {
        return new URL(url).protocol === "https:";
    } catch {
        return false;
    }
};

const isInstagram = (url) => {
    try {
        const u = new URL(url);
        return u.protocol === "https:" && u.hostname === "www.instagram.com" && url.startsWith("https://www.instagram.com/");
    } catch {
        return false;
    }
};

// Corta a legenda nas ~100 letras, sem partir a última palavra
const resumir = (texto) => {
    const limpo = String(texto || "").replace(/\s+/g, " ").trim();
    if (limpo.length <= LIMITE_LEGENDA) return limpo;
    const corte = limpo.slice(0, LIMITE_LEGENDA);
    const espaco = corte.lastIndexOf(" ");
    return `${(espaco > 60 ? corte.slice(0, espaco) : corte).replace(/[\s.,;:!?-]+$/, "")}…`;
};

const validar = (post) => {
    const permalink = post.permalink;
    const sizes = post.sizes || {};
    const medium = sizes.medium?.mediaUrl;
    const large = sizes.large?.mediaUrl;

    if (!isInstagram(permalink)) {
        console.warn("Galeria: post ignorado, permalink inválido:", permalink);
        return null;
    }
    if (!isHttps(medium) || (large && !isHttps(large))) {
        console.warn("Galeria: post ignorado, imagem de capa inválida:", permalink);
        return null;
    }

    const srcSet = [
        `${medium} ${sizes.medium.width || 700}w`,
        large && `${large} ${sizes.large.width || 1000}w`,
    ].filter(Boolean).join(", ");

    return {
        id: post.id || permalink,
        permalink,
        src: medium,
        srcSet,
        legenda: resumir(post.prunedCaption),
    };
};

// O Behold envia datas como "2026-10-15T20:00:10+0000"; o Safari só aceita "+00:00"
const data = (timestamp) => {
    const t = Date.parse(String(timestamp || "").replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
    return Number.isNaN(t) ? 0 : t;
};

const prepararVideos = (dados) => {
    // O Behold devolve { posts: [...] } (formato atual) ou só a lista (formato antigo)
    const posts = Array.isArray(dados) ? dados : dados?.posts;
    if (!Array.isArray(posts)) throw new Error("Formato do feed inesperado");

    return posts
        .filter(post => post?.mediaType === "VIDEO")
        .sort((a, b) => data(b.timestamp) - data(a.timestamp))
        .map(validar)
        .filter(Boolean)
        .slice(0, MAX_VIDEOS);
};

const PlayIcon = () => (
    <svg className="video-play" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <circle cx="32" cy="32" r="31" />
        <path d="M26 20 L46 32 L26 44 Z" />
    </svg>
);

const Gallery = () => {
    const [estado, setEstado] = useState("a-carregar"); // a-carregar | pronto | erro
    const [videos, setVideos] = useState([]);

    useEffect(() => {
        if (!FEED_URL) {
            console.warn("Galeria: REACT_APP_BEHOLD_FEED_URL não está definida.");
            setEstado("erro");
            return;
        }

        const controller = new AbortController();
        fetch(FEED_URL, { signal: controller.signal })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(dados => {
                setVideos(prepararVideos(dados));
                setEstado("pronto");
            })
            .catch(err => {
                if (err.name === "AbortError") return;
                console.warn("Galeria: não foi possível carregar o feed do Instagram.", err);
                setEstado("erro");
            });

        return () => controller.abort();
    }, []);

    const semVideos = estado === "erro" || (estado === "pronto" && videos.length === 0);

    return (
        <section id="gallery" className="gallery" aria-busy={estado === "a-carregar"}>
            <h2 className="section-title">Galeria</h2>

            {estado === "a-carregar" && (
                <>
                    <p className="gallery-sr" role="status">A carregar vídeos…</p>
                    <ul className="gallery-grid" aria-hidden="true">
                        {Array.from({ length: MAX_VIDEOS }, (_, i) => (
                            <li key={i} className="video-skeleton" />
                        ))}
                    </ul>
                </>
            )}

            {estado === "pronto" && videos.length > 0 && (
                <ul className="gallery-grid">
                    {videos.map(video => (
                        <li key={video.id}>
                            <a
                                className="video-card"
                                href={video.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={video.legenda
                                    ? `Ver vídeo no Instagram: ${video.legenda}`
                                    : "Ver vídeo no Instagram"}
                            >
                                <img
                                    src={video.src}
                                    srcSet={video.srcSet}
                                    sizes="(max-width: 600px) 50vw, (max-width: 1200px) 33vw, 380px"
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                                <PlayIcon />
                                {video.legenda && (
                                    <span className="video-legenda" aria-hidden="true">{video.legenda}</span>
                                )}
                            </a>
                        </li>
                    ))}
                </ul>
            )}

            {semVideos && (
                <p className="gallery-vazio">
                    De momento não conseguimos mostrar os vídeos.{" "}
                    <a href={PERFIL_INSTAGRAM} target="_blank" rel="noopener noreferrer">
                        Veja os treinos no nosso Instagram
                    </a>
                </p>
            )}
        </section>
    );
};

export default Gallery;
